import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { MGMT } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { useAuth } from "@/context/AuthContext";
import { ResourceForm } from "@/components/ResourceForm";

export const CrudPage = ({ resource, fields, columns, writeRoles, params = {}, defaults = {}, title, testid,
  rowClass, extraActions, newLabel = "Nuovo", editRoles, onChanged }) => {
  const { hotelId, hotelName } = useHotel();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const paramsKey = JSON.stringify(params);

  const load = useCallback(async () => {
    setLoading(true);
    const p = { ...JSON.parse(paramsKey) };
    if (hotelId && hotelId !== "all") p.hotel_id = hotelId;
    try { setRows((await api.get(`/${resource}`, { params: p })).data); }
    catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [resource, hotelId, paramsKey]);

  useEffect(() => { load(); }, [load]);

  const canWrite = writeRoles.includes(user?.role);
  const canEdit = (editRoles || writeRoles).includes(user?.role);
  const canDelete = MGMT.includes(user?.role);
  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(s)));
  }, [rows, q]);

  const save = async (v) => {
    try {
      if (v.id) await api.put(`/${resource}/${v.id}`, v);
      else await api.post(`/${resource}`, v);
      toast.success("Salvato");
      setEditing(null);
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Eliminare definitivamente questo elemento?")) return;
    try { await api.delete(`/${resource}/${r.id}`); toast.success("Eliminato"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openNew = () => setEditing({ hotel_id: hotelId !== "all" ? hotelId : undefined, ...(typeof defaults === "function" ? defaults() : defaults) });
  const cols = hotelId === "all" ? [{ key: "hotel_id", label: "Struttura", render: (r) => hotelName(r.hotel_id) }, ...columns] : columns;

  return (
    <div className="space-y-4" data-testid={`${testid}-section`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid={`${testid}-search`} />
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid={`${testid}-new-btn`} className="gap-2"><Plus className="w-4 h-4" />{newLabel}</Button>
        )}
      </div>
      <div className="panel overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              {cols.map((c) => <TableHead key={c.key} className="font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{c.label}</TableHead>)}
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={cols.length + 1} className="text-center text-slate-500 py-10">Caricamento…</TableCell></TableRow>}
            {!loading && !filtered.length && (
              <TableRow><TableCell colSpan={cols.length + 1} className="text-center text-slate-500 py-10" data-testid={`${testid}-empty`}>Nessun elemento</TableCell></TableRow>
            )}
            {!loading && filtered.map((r) => (
              <TableRow key={r.id} className={`border-slate-200 hover:bg-slate-50 ${rowClass?.(r) || ""}`} data-testid={`${testid}-row`}>
                {cols.map((c) => <TableCell key={c.key} className="text-sm text-slate-800 align-top">{c.render ? c.render(r) : r[c.key] || "—"}</TableCell>)}
                <TableCell className="text-right whitespace-nowrap">
                  {extraActions?.(r, load)}
                  {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(r)} data-testid={`${testid}-edit-btn`}><Pencil className="w-4 h-4" /></Button>}
                  {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`${testid}-delete-btn`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-xs text-slate-500 font-mono" data-testid={`${testid}-count`}>{filtered.length} elementi</div>
      <ResourceForm open={!!editing} initial={editing} onClose={() => setEditing(null)} onSubmit={(v) => save({ ...params, ...v })}
        fields={fields} title={editing?.id ? `Modifica · ${title}` : `${newLabel} · ${title}`} testid={`${testid}-form`} />
    </div>
  );
};
